import { useSeoMeta } from '@unhead/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Zap, Target, ChevronRight, Radio, Cpu, Lock, Menu, X } from 'lucide-react';
import { useState } from 'react';

const Index = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useSeoMeta({
    title: 'Attritable System Technologies | Advanced Military Defense Solutions',
    description: 'Leading provider of cutting-edge attritable unmanned systems and advanced defense technologies for modern warfare.',
  });

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img src="/images/logo.png" alt="Attritable System Technologies" className="h-10 sm:h-12 w-auto object-contain" />
              <span className="text-sm sm:text-lg font-bold tracking-tight hidden sm:inline">
                ATTRITABLE <span className="text-primary">SYSTEM</span> TECHNOLOGIES
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <a href="#solutions" className="text-sm hover:text-primary transition-colors">Solutions</a>
              <a href="#capabilities" className="text-sm hover:text-primary transition-colors">Capabilities</a>
              <a href="#technology" className="text-sm hover:text-primary transition-colors">Technology</a>
              <Button 
                variant="outline" 
                className="border-primary text-primary hover:bg-primary hover:text-black"
                asChild
              >
                <a href="mailto:adam@attrsys.com">Contact Us</a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4 space-y-3">
              <a 
                href="#solutions" 
                className="block py-2 px-4 hover:bg-white/5 rounded-lg transition-colors"
                onClick={closeMobileMenu}
              >
                Solutions
              </a>
              <a 
                href="#capabilities" 
                className="block py-2 px-4 hover:bg-white/5 rounded-lg transition-colors"
                onClick={closeMobileMenu}
              >
                Capabilities
              </a>
              <a 
                href="#technology" 
                className="block py-2 px-4 hover:bg-white/5 rounded-lg transition-colors"
                onClick={closeMobileMenu}
              >
                Technology
              </a>
              <Button 
                variant="outline" 
                className="w-full border-primary text-primary hover:bg-primary hover:text-black"
                asChild
              >
                <a href="mailto:adam@attrsys.com">Contact Us</a>
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden isolate min-h-[85vh] sm:min-h-screen flex items-center">
        {/* Hero background image */}
        <div className="absolute inset-0 -z-10">
          <img 
            src="/images/hero-bg.jpg" 
            alt="Military field operations" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/70" />
          {/* Gold gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-black/50 to-black" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 w-full">
          <div className="max-w-5xl mx-auto text-center space-y-6 sm:space-y-8">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-primary/10 border border-primary/30 rounded-full text-xs sm:text-sm text-primary mb-2 sm:mb-4">
              <span className="inline-block w-2 h-2 bg-primary rounded-full mr-2 animate-pulse" />
              Next-Generation Tactical Systems
            </div>
            
            <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-tight px-4 sm:px-0">
              STRATEGIC
              <br />
              <span className="text-primary">SUPERIORITY</span>
              <br />
              DELIVERED
            </h1>
            
            <p className="text-base sm:text-xl md:text-2xl text-white/70 max-w-3xl mx-auto leading-relaxed px-4 sm:px-0">
              Delivering innovative, high-quality, Berry Amendment compliant tactical transport solutions that enhance operational effectiveness and mission readiness.
            </p>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 pt-4 sm:pt-6 px-4 sm:px-0">
              <Button 
                size="lg" 
                className="bg-primary text-black hover:bg-primary/90 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg group w-full sm:w-auto min-h-[48px]" 
                asChild
              >
                <a href="#solutions">
                  Explore Solutions
                  <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 hover:bg-white/5 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto min-h-[48px]" 
                asChild
              >
                <a href="mailto:adam@attrsys.com">Request Briefing</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-black to-zinc-950 scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 px-4 sm:px-0">
              COMBAT-PROVEN <span className="text-primary">SOLUTIONS</span>
            </h2>
            <p className="text-white/60 text-base sm:text-lg max-w-2xl mx-auto px-4 sm:px-0">
              Engineered for the most demanding operational environments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: Shield,
                title: 'Compliance as a Strategic Moat',
                description: '',
                features: [
                  '100% Berry Amendment compliant supply chain',
                  'Robust security and quality assurance practices',
                  'Meeting tomorrow\'s requirements today',
                  'Reduced procurement friction for government buyers'
                ],
              },
              {
                icon: Target,
                title: 'Agility & Niche Focus',
                description: '',
                features: [
                  'Small, veteran-led firm with rapid decision-making',
                  'Singular focus on sUAS tactical transport',
                  'Lean operational model for competitive pricing',
                  'Strategic manufacturing partnerships for scalability'
                ],
              },
              {
                icon: Shield,
                title: 'Veteran Leadership & Expertise',
                description: '',
                features: [
                  'Founded and led by combat veterans',
                  'Direct operational experience in the field',
                  'Warfighter perspective driving product development',
                  'Deep understanding of operational requirements'
                ],
              },
            ].map((solution, idx) => (
              <Card key={idx} className="bg-zinc-900/50 border-white/10 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group">
                <CardContent className="p-5 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
                  <div className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <solution.icon className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-primary" />
                  </div>
                  
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-3 group-hover:text-primary transition-colors uppercase">
                      {solution.title}
                    </h3>
                    {solution.description && (
                      <p className="text-white/60 leading-relaxed mb-4">
                        {solution.description}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 sm:space-y-3">
                    {solution.features.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-start text-xs sm:text-sm text-white/80">
                        <span className="text-primary mr-2 sm:mr-3 flex-shrink-0 mt-0.5">✓</span>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="py-12 sm:py-16 md:py-20 bg-zinc-950 scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
                  UNMATCHED <span className="text-primary">CAPABILITIES</span>
                </h2>
                <p className="text-white/60 text-base sm:text-lg leading-relaxed">
                  Our attritable systems combine affordability with advanced performance, 
                  enabling mass deployment without compromising mission effectiveness.
                </p>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {[
                  {
                    icon: Shield,
                    title: 'Rapid Deployment',
                    description: 'Modular architecture enables quick configuration and field deployment in under 30 minutes.',
                  },
                ].map((capability, idx) => (
                  <div key={idx} className="flex gap-3 sm:gap-4 p-4 sm:p-6 bg-black/40 border border-white/5 rounded-lg hover:border-primary/30 transition-all">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 sm:h-12 sm:w-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <capability.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">{capability.title}</h3>
                      <p className="text-white/60 text-sm sm:text-base">{capability.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative order-first lg:order-last">
              <div className="aspect-square bg-gradient-to-br from-primary/20 to-transparent rounded-xl sm:rounded-2xl overflow-hidden border border-white/10 relative isolate">
                <div className="absolute inset-0 -z-10">
                  <div 
                    className="absolute inset-0 opacity-30"
                    style={{
                      backgroundImage: `radial-gradient(circle at center, rgb(255 204 0 / 0.2) 1px, transparent 1px)`,
                      backgroundSize: '20px 20px',
                    }}
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center p-8 sm:p-12">
                  <img src="/images/logo.png" alt="Attritable System Technologies" className="w-full h-full object-contain opacity-60" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology / Product Section */}
      <section id="technology" className="py-12 sm:py-16 md:py-24 bg-black scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6">

          {/* Section header */}
          <div className="text-center mb-10 sm:mb-14 md:mb-20">
            <div className="inline-block px-3 sm:px-4 py-1.5 bg-primary/10 border border-primary/30 rounded-full text-xs sm:text-sm text-primary mb-4">
              <span className="inline-block w-2 h-2 bg-primary rounded-full mr-2 animate-pulse" />
              Berry Amendment Compliant
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 px-4 sm:px-0">
              AST-1 <span className="text-primary">TACTICAL TRANSPORT</span> SYSTEM
            </h2>
            <p className="text-white/60 text-base sm:text-lg max-w-3xl mx-auto px-4 sm:px-0">
              A purpose-built sUAS transport and launch platform engineered for the modern warfighter — 
              rugged, rapid-deploy, and fully compliant.
            </p>
          </div>

          {/* Schematic + specs layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-start max-w-6xl mx-auto mb-16 sm:mb-20">

            {/* Schematic image */}
            <div className="relative group">
              <div className="relative rounded-xl sm:rounded-2xl overflow-hidden border border-primary/20 bg-zinc-950 shadow-[0_0_60px_rgba(255,204,0,0.07)]">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
                <img
                  src="/images/product-schematic.webp"
                  alt="AST-1 Tactical Transport System — Orthographic Schematic"
                  className="w-full h-auto object-contain p-2 sm:p-4"
                />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                  <span className="text-[10px] sm:text-xs text-primary/70 font-mono uppercase tracking-widest">
                    AST-1 / Front Elevation
                  </span>
                  <span className="text-[10px] sm:text-xs text-white/30 font-mono">
                    ATTRITABLE SYSTEM TECHNOLOGIES
                  </span>
                </div>
              </div>
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-primary/60 rounded-tl-xl pointer-events-none" />
              <div className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-primary/60 rounded-tr-xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-primary/60 rounded-bl-xl pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-primary/60 rounded-br-xl pointer-events-none" />
            </div>

            {/* Specs */}
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-1 text-primary uppercase tracking-wide">
                  System Specifications
                </h3>
                <p className="text-white/50 text-xs sm:text-sm">AST-1 Rev. A — Current Production Variant</p>
              </div>

              <div className="space-y-3">
                {[
                  { label: 'Configuration', value: 'Tactical backpack with integrated drone launch cradle' },
                  { label: 'Fabric', value: 'MultiCam® / OCP-compliant ripstop nylon, Berry Amendment certified' },
                  { label: 'MOLLE System', value: 'Full laser-cut PALS webbing — front, sides, and base panels' },
                  { label: 'Drone Interface', value: 'Modular top-mount platform, adjustable arm retention' },
                  { label: 'Closure System', value: 'Heavy-duty YKK® zippers with quick-release buckle restraints' },
                  { label: 'Carry System', value: 'Padded shoulder harness with load-transfer hip belt' },
                  { label: 'Compatibility', value: 'Fits 5" – 7" frame class sUAS; custom cradles available' },
                  { label: 'Compliance', value: '100% Berry Amendment / NDAA Section 889 compliant' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex gap-3 sm:gap-4 py-2.5 sm:py-3 border-b border-white/5">
                    <span className="text-primary text-xs sm:text-sm font-mono uppercase tracking-wide w-28 sm:w-36 flex-shrink-0 pt-0.5">
                      {label}
                    </span>
                    <span className="text-white/70 text-xs sm:text-sm leading-relaxed">{value}</span>
                  </div>
                ))}
              </div>

              <div className="pt-2">
                <Button
                  size="lg"
                  className="bg-primary text-black hover:bg-primary/90 px-6 sm:px-8 py-4 sm:py-5 text-sm sm:text-base w-full sm:w-auto min-h-[48px] font-bold"
                  asChild
                >
                  <a href="mailto:adam@attrsys.com">Request Product Briefing</a>
                </Button>
              </div>
            </div>
          </div>

          {/* Field photos gallery */}
          <div className="max-w-6xl mx-auto">
            <h3 className="text-lg sm:text-xl font-bold text-white/80 mb-4 sm:mb-6 uppercase tracking-wider text-center">
              Field Operations Gallery
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
              {[
                { src: '/images/product-1.jpg', alt: 'AST-1 system with operators in field' },
                { src: '/images/product-2.jpg', alt: 'Drone mounted on AST-1 backpack, top view' },
                { src: '/images/product-3.jpg', alt: 'Two operators with AST-1 system in field' },
                { src: '/images/product-4.jpg', alt: 'Backpack deployed open, drone extraction sequence' },
              ].map((photo, idx) => (
                <div key={idx} className="relative group overflow-hidden rounded-lg sm:rounded-xl aspect-[3/4] bg-zinc-900">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 sm:p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <p className="text-white text-[10px] sm:text-xs leading-tight">{photo.alt}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-zinc-950 to-black">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6 sm:space-y-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold px-4 sm:px-0">
              READY TO ADVANCE YOUR <span className="text-primary">TACTICAL EDGE?</span>
            </h2>
            <p className="text-white/60 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto px-4 sm:px-0">
              Contact our defense solutions team to discuss how our attritable systems 
              can enhance your operational capabilities.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2 sm:pt-4 px-4 sm:px-0">
              <Button 
                size="lg" 
                className="bg-primary text-black hover:bg-primary/90 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto min-h-[48px]" 
                asChild
              >
                <a href="mailto:adam@attrsys.com">Schedule Consultation</a>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/20 hover:bg-white/5 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg w-full sm:w-auto min-h-[48px]" 
                asChild
              >
                <a href="/brochure.pdf">Download Catalog</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 sm:py-10 md:py-12 bg-black">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="sm:col-span-2">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                <img src="/images/logo.png" alt="Attritable System Technologies" className="h-6 sm:h-8 w-auto object-contain" />
                <span className="font-bold text-sm sm:text-base">ATTRITABLE SYSTEM TECHNOLOGIES</span>
              </div>
              <p className="text-white/60 text-xs sm:text-sm max-w-md">
               Service-Disabled Veteran-Owned Business (SDVOSB)
               Founded by Combat Veteran and Army Aviator Adam Freeman
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-3 sm:mb-4 text-primary text-sm sm:text-base">Company</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-white/60">
                <li><a href="#" className="hover:text-primary transition-colors inline-block py-1">About</a></li>
                <li><a href="#" className="hover:text-primary transition-colors inline-block py-1">Careers</a></li>
                <li><a href="#" className="hover:text-primary transition-colors inline-block py-1">Contact</a></li>
              </ul>
            </div>
            <div className="lg:col-span-2 flex justify-center lg:justify-end items-start">
              <div className="w-32 sm:w-40 lg:w-44">
                <img 
                  src="/images/sdvosb.png" 
                  alt="Service-Disabled Veteran-Owned Small Business Certified" 
                  className="w-full h-auto object-contain opacity-80 hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 text-xs sm:text-sm text-white/60 text-center sm:text-left">
            <p>© 2026 Attritable System Technologies LLC. - All rights reserved. - <a href="https://purelymail.com/user/login" className="hover:text-primary transition-colors">login</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
